import React, { useState, useEffect, useRef, memo } from "react";
import { NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, LogIn, LogOut, Mail, Lock, XCircle } from "lucide-react";
import logo from "../assets/Img/airaa-logo-white.webp";
import { useTranslation } from "react-i18next";

// ── Auth Modal ────────────────────────────────────────────────────────────────
const AuthModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      style={{ zIndex: 9999 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-7 relative border border-slate-100">
        <button onClick={onClose} aria-label="Close modal" className="absolute right-4 top-4 text-slate-400 hover:text-slate-600 transition">
          <XCircle size={20} />
        </button>
        <h2 className="text-2xl font-bold text-slate-800 mb-5">{title}</h2>
        {children}
      </div>
    </div>
  );
};

// ── Live Clock ────────────────────────────────────────────────────────────────
const LiveClock = memo(() => {
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date().toLocaleTimeString()), 1000);
    return () => clearInterval(interval);
  }, []);
  return <span className="text-xs text-slate-400 tabular-nums">{currentTime}</span>;
});

// ── Main Navbar ───────────────────────────────────────────────────────────────
const Navbar = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      setIsDesktop(window.innerWidth >= 1024);
      if (window.innerWidth >= 1024) setIsOpen(false);
    };
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const aboutTimer = useRef(null);
  const servicesTimer = useRef(null);

  // Auth state
  const [auth, setAuth] = useState({ isAuthenticated: false, user: null, token: null });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const authTimer = useRef(null);
  const [authDropdownOpen, setAuthDropdownOpen] = useState(false);

  useEffect(() => {
    if (document.getElementById("google-translate-script")) return;
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        { pageLanguage: "en", includedLanguages: "hi,en,ro,ar", autoDisplay: false },
        "google_translate_element"
      );
    };
    const script = document.createElement("script");
    script.id = "google-translate-script";
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const handleAuthEnter = () => { clearTimeout(authTimer.current); setAuthDropdownOpen(true); };
  const handleAuthLeave = () => { authTimer.current = setTimeout(() => setAuthDropdownOpen(false), 120); };

  useEffect(() => {
    const storedAuth = localStorage.getItem("airaa_auth");
    if (storedAuth) {
      try { setAuth(JSON.parse(storedAuth)); } catch (e) { }
    }
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated) localStorage.setItem("airaa_auth", JSON.stringify(auth));
    else localStorage.removeItem("airaa_auth");
  }, [auth]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) { setAuthError("Please enter both email and password"); return; }
    if (loginEmail !== "admin@airaa.co.in" || loginPassword !== "admin@airaa2026") { setAuthError("Invalid credentials"); return; }
    setAuth({ isAuthenticated: true, user: { email: loginEmail, name: "admin", role: "admin" }, token: "mock-jwt-token-" + Date.now() });
    setShowLoginModal(false); setLoginEmail(""); setLoginPassword(""); setAuthError("");
  };

  const handleLogout = () => { setAuth({ isAuthenticated: false, user: null, token: null }); setIsOpen(false); };

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") { setShowLoginModal(false); setIsOpen(false); } };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleAboutEnter = () => { clearTimeout(aboutTimer.current); setAboutOpen(true); };
  const handleAboutLeave = () => { aboutTimer.current = setTimeout(() => setAboutOpen(false), 120); };
  const handleServicesEnter = () => { clearTimeout(servicesTimer.current); setServicesOpen(true); };
  const handleServicesLeave = () => { servicesTimer.current = setTimeout(() => setServicesOpen(false), 120); };

  const dropdownMenu = "absolute left-0 top-full mt-2 w-56 bg-white/95 backdrop-blur-xl shadow-2xl rounded-xl overflow-hidden border border-slate-100";
  const dropdownItem = "block px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150";

  const navLinkClass = ({ isActive }) =>
    isActive
      ? "text-emerald-300 font-bold"
      : "text-white/90 hover:text-white transition-colors duration-150 font-medium";

  return (
    <>
      <nav className="fixed top-0 left-0 w-full" style={{ zIndex: 1000 }}>

        {/* ── Top identity bar ── */}
        <div
          // className="w-full bg-white border-b border-slate-100"
          style={{
            maxHeight: isScrolled ? "0px" : "72px",
            opacity: isScrolled ? 0 : 1,
            overflow: "hidden",
            transition: "max-height 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease",
            pointerEvents: isScrolled ? "none" : "auto",
          }}
        >
          <div className="container mx-auto px-4 py-2 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src={logo} alt="AIRAA Logo" className="w-11 h-11 object-contain flex-shrink-0" />
              <div>
                <span className="block text-xs sm:text-sm font-bold text-blue-900 uppercase leading-tight tracking-wide">
                  ALL INDIA RECRUITING AGENCY ASSOCIATION
                </span>
                <span className="block text-[11px] text-blue-700 leading-tight">
                  अखिल भारतीय भर्ती एजेंसी एसोसिएशन
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {isDesktop && (
                <>
                  <a href="tel:+917738355865" className="text-sm font-semibold text-blue-700 hover:text-blue-900 transition-colors">
                    +91 7738355865
                  </a>
                  <LiveClock />
                </>
              )}
              <div id="google_translate_element" className="goog-translate-wrapper" />
            </div>
          </div>
        </div>

        {/* ── Main nav bar ── */}
        <div
          className={`w-full transition-all duration-300 ${
            isScrolled
              ? "bg-blue-950/95 backdrop-blur-xl shadow-xl border-b border-white/10"
              : "bg-gradient-to-r from-blue-950 via-blue-900 to-green-900"
          }`}
        >
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">

            {/* Mobile: brand */}
            {!isDesktop && (
              <NavLink to="/" className="flex items-center gap-2" onClick={() => setIsOpen(false)}>
                <img src={logo} alt="AIRAA" className="w-8 h-8 object-contain" />
                <span className="text-white text-xs font-semibold uppercase tracking-wide">AIRAA</span>
              </NavLink>
            )}

            {/* ── Desktop links ── */}
            {isDesktop && (
              <div className="flex items-center gap-5 xl:gap-7 text-sm xl:text-[13px] font-medium tracking-wide">
                <NavLink className={navLinkClass} to="/">{t("home")}</NavLink>

                {/* About dropdown */}
                <div className="relative" style={{ zIndex: 1001 }} onMouseEnter={handleAboutEnter} onMouseLeave={handleAboutLeave}>
                  <button aria-expanded={aboutOpen} className="flex items-center gap-1 text-white/90 hover:text-white transition-colors whitespace-nowrap font-medium">
                    <span>{t("about")}</span>
                    <ChevronDown size={13} className={`transition-transform duration-200 ${aboutOpen ? "rotate-180" : ""}`} />
                  </button>
                  {aboutOpen && (
                    <div className={dropdownMenu} style={{ zIndex: 1002 }}>
                      {[
                        { to: "/about-airaa", label: t("about") },
                        { to: "/gallery", label: t("gallerytitle") },
                        { to: "/presidentmessage", label: t("message") },
                        { to: "/news", label: t("news") },
                        { to: "/certificate", label: "Membership Certificates (Chambers & Associations)" },
                      ].map(({ to, label }) => (
                        <NavLink key={to} to={to} className={dropdownItem} onClick={() => setAboutOpen(false)}>{label}</NavLink>
                      ))}
                    </div>
                  )}
                </div>

                {/* Services dropdown */}
                <div className="relative" style={{ zIndex: 1001 }} onMouseEnter={handleServicesEnter} onMouseLeave={handleServicesLeave}>
                  <button aria-expanded={servicesOpen} className="flex items-center gap-1 text-white/90 hover:text-white transition-colors whitespace-nowrap font-medium">
                    <span>{t("Services")}</span>
                    <ChevronDown size={13} className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {servicesOpen && (
                    <div className={dropdownMenu} style={{ zIndex: 1002 }}>
                      {[
                        { to: "/why-join-airaa", label: t("Why Join AIRAA") },
                        { to: "/member-services", label: "Membership & Services" },
                        { to: "/registration", label: t("Registration") },
                        { to: "/membership", label: "Membership & Certificate" },
                        { to: "/helpline", label: t("Helpline") },
                      ].map(({ to, label }) => (
                        <NavLink key={to} to={to} className={dropdownItem} onClick={() => setServicesOpen(false)}>{label}</NavLink>
                      ))}
                    </div>
                  )}
                </div>

                <NavLink className={navLinkClass} to="/socialresponsibility">Initiatives</NavLink>
                <NavLink className={navLinkClass} to="/importantlinks">{t("Important Links")}</NavLink>
                <NavLink className={navLinkClass} to="/contact-us">{t("contact")}</NavLink>
              </div>
            )}

            {/* Register CTA (desktop) */}
            {isDesktop && (
              <NavLink
                to="/registration"
                className="ml-4 inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-full shadow-lg shadow-emerald-600/30 transition-all duration-200 hover:scale-105 active:scale-95"
              >
                Join AIRAA
              </NavLink>
            )}

            {/* ── Mobile hamburger ── */}
            {!isDesktop && (
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-white p-1 ml-auto"
                aria-label="Toggle menu"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
          </div>

          {/* ── Mobile menu ── */}
          {!isDesktop && isOpen && (
            <div className="bg-blue-950/98 backdrop-blur-xl border-t border-white/10 max-h-screen overflow-y-auto">

              <NavLink to="/" className={({ isActive }) => `block px-5 py-3.5 text-sm font-medium border-b border-white/10 ${isActive ? "text-emerald-300 bg-white/5" : "text-white/80 hover:text-white hover:bg-white/5"}`} onClick={() => setIsOpen(false)}>
                {t("home")}
              </NavLink>

              {/* Mobile About accordion */}
              <div className="border-b border-white/10">
                <button className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-white/80 hover:bg-white/5 transition" onClick={() => setMobileAboutOpen(!mobileAboutOpen)}>
                  <span>{t("about")}</span>
                  <ChevronDown size={15} className={`transition-transform duration-200 ${mobileAboutOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileAboutOpen && (
                  <div className="bg-white/5">
                    {[
                      { to: "/about-airaa", label: t("about") },
                      { to: "/gallery", label: t("gallerytitle") },
                      { to: "/presidentmessage", label: t("message") },
                      { to: "/news", label: t("news") },
                      { to: "/certificate", label: "Membership Certificates (Chambers & Associations)" },
                    ].map(({ to, label }) => (
                      <NavLink key={to} to={to} className={({ isActive }) => `block pl-8 pr-5 py-2.5 text-sm ${isActive ? "text-sky-300" : "text-white/70 hover:text-white hover:bg-white/5"} transition`} onClick={() => { setMobileAboutOpen(false); setIsOpen(false); }}>
                        {label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Services accordion */}
              <div className="border-b border-white/10">
                <button className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-white/80 hover:bg-white/5 transition" onClick={() => setMobileServicesOpen(!mobileServicesOpen)}>
                  <span>{t("Services")}</span>
                  <ChevronDown size={15} className={`transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileServicesOpen && (
                  <div className="bg-white/5">
                    {[
                      { to: "/why-join-airaa", label: t("Why Join AIRAA") },
                      { to: "/member-services", label: "Membership & Services" },
                      { to: "/registration", label: t("Registration") },
                      { to: "/membership", label: "Membership & Certificate" },
                      { to: "/helpline", label: t("Helpline") },
                    ].map(({ to, label }) => (
                      <NavLink key={to} to={to} className={({ isActive }) => `block pl-8 pr-5 py-2.5 text-sm ${isActive ? "text-sky-300" : "text-white/70 hover:text-white hover:bg-white/5"} transition`} onClick={() => { setMobileServicesOpen(false); setIsOpen(false); }}>
                        {label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>

              {[
                { to: "/socialresponsibility", label: "Initiatives" },
                { to: "/importantlinks", label: t("Important Links") },
                { to: "/contact-us", label: t("contact") },
              ].map(({ to, label }) => (
                <NavLink key={to} to={to} className={({ isActive }) => `block px-5 py-3.5 text-sm font-medium border-b border-white/10 ${isActive ? "text-emerald-300 bg-white/5" : "text-white/80 hover:text-white hover:bg-white/5"}`} onClick={() => setIsOpen(false)}>
                  {label}
                </NavLink>
              ))}

              <div className="px-5 py-4">
                <NavLink to="/registration" className="block w-full text-center py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded-full transition-colors" onClick={() => setIsOpen(false)}>
                  Join AIRAA
                </NavLink>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* ── Login Modal ── */}
      <AuthModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} title="Login to AIRAA">
        <form onSubmit={handleLogin} className="space-y-4">
          {authError && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{authError}</p>}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
              <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="you@example.com" autoFocus />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
              <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="••••••••" />
            </div>
          </div>
          <button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white py-2.5 rounded-xl transition font-semibold text-sm">
            Sign In
          </button>
          <p className="text-center text-sm text-slate-500">
            New member?{" "}
            <NavLink to="/registration" onClick={() => setShowLoginModal(false)} className="text-blue-700 hover:underline font-semibold">
              Register here
            </NavLink>
          </p>
        </form>
      </AuthModal>
    </>
  );
};

export default Navbar;
